import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Sparkle, Bank, CreditCard, PenNib, ArrowSquareOut, CheckCircle, CircleNotch } from "@phosphor-icons/react";

const money = (n) => `$${(n || 0).toLocaleString()}`;

export function FinalFlow({ engagementId, open, onOpenChange }) {
  const qc = useQueryClient();
  const [step, setStep] = useState(0);
  const [typedName, setTypedName] = useState("");
  const [consent, setConsent] = useState(false);

  useEffect(() => { if (open) { setStep(0); setTypedName(""); setConsent(false); } }, [open, engagementId]);

  const { data, refetch, isLoading } = useQuery({
    queryKey: ["final-flow", engagementId],
    queryFn: () => api.get(`/portal/final-flow/${engagementId}`).then((r) => r.data),
    enabled: open && !!engagementId,
  });

  const gen = useMutation({
    mutationFn: () => api.post(`/portal/return-summary/${engagementId}/generate`),
    onSuccess: () => refetch(),
    onError: (e) => toast.error(errText(e)),
  });
  const pay = useMutation({
    mutationFn: ({ id, method }) => api.post(`/portal/invoices/${id}/pay`, { method }),
    onSuccess: () => { toast.success("Payment received — thank you!"); refetch(); qc.invalidateQueries({ queryKey: ["portal-extras"] }); },
    onError: (e) => toast.error(errText(e)),
  });
  const sign = useMutation({
    mutationFn: () => api.post(`/portal/sign/${data.envelope.id}`, { typed_name: typedName, consent }),
    onSuccess: () => { setStep(3); qc.invalidateQueries({ queryKey: ["portal"] }); qc.invalidateQueries({ queryKey: ["portal-extras"] }); },
    onError: (e) => toast.error(errText(e)),
  });

  const s = data?.summary;
  const invoices = data?.unpaid_invoices || [];
  const env = data?.envelope;
  const labels = ["Review summary", "Pay invoice", "Sign"];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" aria-describedby={undefined} data-testid="final-flow-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Finish your {data?.engagement?.service_type} {data?.engagement?.tax_year} return</DialogTitle>
        </DialogHeader>

        {step < 3 && (
          <div className="flex items-center gap-1 mb-1">
            {labels.map((l, i) => (
              <div key={l} className="flex-1">
                <div className={`h-1 rounded-full ${i <= step ? "bg-zinc-900" : "bg-zinc-200"}`} />
                <p className={`text-[10px] mt-1 ${i === step ? "text-zinc-900 font-medium" : "text-zinc-400"}`}>{i + 1}. {l}</p>
              </div>
            ))}
          </div>
        )}

        {isLoading && <p className="text-xs text-zinc-400 flex items-center gap-1"><CircleNotch size={13} className="animate-spin" /> Loading…</p>}

        {!isLoading && step === 0 && (
          <div className="space-y-3" data-testid="final-flow-step-summary">
            {!s ? (
              <div className="text-center py-6">
                <p className="text-sm text-zinc-600 mb-3">Get a plain-English breakdown of your return — no tax jargon.</p>
                <Button data-testid="generate-summary-btn" disabled={gen.isPending} onClick={() => gen.mutate()}>
                  {gen.isPending ? <CircleNotch size={14} className="animate-spin mr-1.5" /> : <Sparkle size={14} className="mr-1.5" />}
                  {gen.isPending ? "Writing your summary…" : "Generate my summary"}
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm font-medium text-zinc-900" data-testid="summary-headline">{s.headline}</p>
                <ul className="space-y-1.5">
                  {(s.bullets || []).map((b, i) => <li key={i} className="text-xs text-zinc-700 flex gap-2"><span className="text-zinc-400">•</span><span>{b}</span></li>)}
                </ul>
                {s.vs_last_year && (
                  <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3" data-testid="summary-vs-last-year">
                    <p className="text-[10px] uppercase tracking-wide text-zinc-400 mb-1">Compared to last year</p>
                    <p className="text-xs text-zinc-700">{s.vs_last_year}</p>
                  </div>
                )}
                {(s.next_steps || []).length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-zinc-400 mb-1">Next steps</p>
                    {(s.next_steps || []).map((n, i) => <p key={i} className="text-xs text-zinc-700">→ {n}</p>)}
                  </div>
                )}
              </div>
            )}
            <Button data-testid="final-flow-next-1" className="w-full" disabled={!s} onClick={() => setStep(1)}>Looks good — continue</Button>
          </div>
        )}

        {!isLoading && step === 1 && (
          <div className="space-y-3" data-testid="final-flow-step-pay">
            {invoices.length === 0 ? (
              <p className="text-sm text-zinc-600 text-center py-4 flex items-center justify-center gap-1.5">
                <CheckCircle size={16} weight="fill" className="text-emerald-500" /> No balance due — you're all paid up.
              </p>
            ) : invoices.map((i) => (
              <div key={i.id} className="rounded-md border border-zinc-200 p-3 flex items-center justify-between" data-testid={`flow-invoice-${i.id}`}>
                <div>
                  <p className="text-xs font-medium text-zinc-900 font-mono">{i.number} · {money(i.amount)}</p>
                  <p className="text-[11px] text-zinc-500">{i.description}</p>
                </div>
                <div className="flex gap-1.5">
                  <Button data-testid={`flow-pay-ach-${i.id}`} size="sm" variant="outline" className="h-7 text-[11px]" disabled={pay.isPending} onClick={() => pay.mutate({ id: i.id, method: "ach" })}><Bank size={12} className="mr-1" /> ACH</Button>
                  <Button data-testid={`flow-pay-card-${i.id}`} size="sm" className="h-7 text-[11px]" disabled={pay.isPending} onClick={() => pay.mutate({ id: i.id, method: "card" })}><CreditCard size={12} className="mr-1" /> Card</Button>
                </div>
              </div>
            ))}
            <Button data-testid="final-flow-next-2" className="w-full" onClick={() => setStep(2)}>
              {invoices.length === 0 ? "Continue to signature" : "Continue (pay later)"}
            </Button>
          </div>
        )}

        {!isLoading && step === 2 && (
          <div className="space-y-3" data-testid="final-flow-step-sign">
            {!env ? (
              <p className="text-sm text-zinc-600 text-center py-4">Nothing awaiting your signature right now.</p>
            ) : env.sign_method === "drake_portals" ? (
              <div className="text-center py-4 space-y-3">
                <p className="text-sm text-zinc-700">"{env.title}" is signed securely via Drake Portals.</p>
                <a data-testid="flow-drake-link" href={env.external_link} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm bg-zinc-900 text-white rounded-md px-4 py-2 hover:bg-zinc-800 transition-colors duration-150">
                  <ArrowSquareOut size={14} /> Open Drake Portals to sign
                </a>
                <p className="text-[11px] text-zinc-400">Your firm will confirm here once Drake notifies them.</p>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-zinc-700">{env.title}</p>
                <Input data-testid="sign-typed-name-input" placeholder="Type your full legal name" value={typedName} onChange={(e) => setTypedName(e.target.value)} />
                <label className="flex items-start gap-2 text-[11px] text-zinc-600 cursor-pointer">
                  <input data-testid="sign-consent-checkbox" type="checkbox" className="mt-0.5" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
                  <span>I intend this typed name to be my legal electronic signature authorizing e-filing of this return (Form 8879 e-signature consent). My IP address and timestamp will be recorded.</span>
                </label>
                <Button data-testid="flow-sign-btn" className="w-full" disabled={!typedName.trim() || !consent || sign.isPending} onClick={() => sign.mutate()}>
                  {sign.isPending ? <CircleNotch size={14} className="animate-spin mr-1.5" /> : <PenNib size={14} className="mr-1.5" />} Sign now
                </Button>
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="text-center py-8" data-testid="final-flow-done">
            <CheckCircle size={40} weight="fill" className="text-emerald-500 mx-auto mb-3" />
            <p className="text-sm font-medium text-zinc-900">All done — your return is queued for e-file!</p>
            <p className="text-xs text-zinc-500 mt-1">Track transmission and IRS acceptance on your return tracker.</p>
            <Button data-testid="final-flow-close-btn" className="mt-4" onClick={() => onOpenChange(false)}>Back to portal</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
